
import { WidgetDragProvider } from "@/contexts/WidgetDragContext";
import type { ReactNode } from 'react'
import { useState, useEffect, useCallback } from 'react'
import { WindowsProvider } from '@/hooks/useWindows'
import WindowContainer from '@/components/system/WindowContainer'
import Taskbar from '@/components/system/Taskbar'
import { useSettingsContext } from '@/contexts/SettingsContext'
import { useAdaptiveTransition } from '@/hooks/useAdaptiveTransition'
import { VoiceAgentProvider } from '@/contexts/VoiceAgentContext'
import { WidgetAreaProvider } from '@/contexts/WidgetAreaContext'
import { WidgetSettingsProvider } from '@/contexts/WidgetSettingsContext'
import { WidgetBar } from '@/components/widgets/bar/WidgetBar'
import { WidgetDndProvider } from '@/components/widgets/WidgetDndProvider'
import { DesktopGridProvider } from '@/contexts/DesktopGridContext'
import { GridDragProvider } from '@/contexts/GridDragContext'
import { ChangelogModal } from '@/components/system/ChangelogModal'
import { CommandPalette } from '@/components/system/CommandPalette'
import { DesktopTicketPanel } from '@/components/system/ticket-panel/DesktopTicketPanel'
import { SelectionTranslationTooltip } from '@/components/system/SelectionTranslationTooltip'
import { useChangelogStartup } from '@/hooks/useChangelogStartup'

type DesktopEnvironmentProps = {
  children: ReactNode;
};

/**
 * DesktopEnvironment
 * Zentralisiert die Desktop-Logik:
 * - kapselt WindowsProvider
 * - rendert WindowContainer (alle Fenster)
 * - rendert Taskbar (Startmenü, Fensterliste, Uhr)
 * - Desktop-Hintergrund basierend auf Einstellungen
 * Übergibt den Inhaltsbereich als children (z. B. DesktopPage).
 */
export default function DesktopEnvironment({
  children,
}: DesktopEnvironmentProps) {
  const { settings, currentTheme } = useSettingsContext();
  const [desktopStyle, setDesktopStyle] = useState<React.CSSProperties>({});
  useAdaptiveTransition();

  // Apply theme CSS class to DOM
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!currentTheme) return;

    const root = document.documentElement;
    const body = document.body;

    // SIMPLIFIED: Don't apply dark/light classes - colors are set directly by SettingsContext
    root.classList.remove('dark', 'light')
    body.classList.remove('dark', 'light')

    // Remove legacy hardcoded theme classes if any remain on the element
    const legacyClasses = [
      'cdb-light', 'cdb-dark',
      'theme-aqua', 'theme-dark-pro', 'theme-nordic',
      'theme-sunset', 'theme-forest', 'theme-ocean',
      'theme-monochrome', 'theme-contrast-dark', 'theme-contrast-light'
    ]
    root.classList.remove(...legacyClasses)
    body.classList.remove(...legacyClasses)
  }, [currentTheme])

  useEffect(() => {
    // Desktop-Hintergrund aus Settings laden
    const applyDesktopBackground = () => {
      try {
        const background = settings.background;

        let styleObj: React.CSSProperties = {};

        if (background.image) {
          const repeatMode =
            background.imageMode === "repeat" ? "repeat" : "no-repeat";
          const sizeMode =
            background.imageMode === "cover"
              ? "cover"
              : background.imageMode === "contain"
              ? "contain"
              : "auto";
          styleObj = {
            backgroundImage: `url(${background.image})`,
            backgroundSize: sizeMode,
            backgroundRepeat: repeatMode,
            backgroundPosition: "center",
          };
        }

        setDesktopStyle(styleObj);
      } catch (error) {
        console.error("Fehler beim Anwenden des Desktop-Hintergrunds:", error);
      }
    };

    applyDesktopBackground();
  }, [settings.background]);

  return (
    <div className="desktop-environment min-h-screen" style={desktopStyle}>
      <VoiceAgentProvider>
        <WidgetSettingsProvider>
          <WidgetAreaProvider>
            <WindowsProvider>
              <DesktopContent>{children}</DesktopContent>
            </WindowsProvider>
          </WidgetAreaProvider>
        </WidgetSettingsProvider>
      </VoiceAgentProvider>
    </div>
  );
}

/**
 * Inner component with access to WindowsProvider
 */
function DesktopContent({ children }: { children: ReactNode }) {
  // Changelog state — opened at start by useChangelogStartup, or by the event below
  const [changelogOpen, setChangelogOpen] = useState(false)
  const [changelogStartupMode, setChangelogStartupMode] = useState(false)

  const showChangelogAtStartup = useCallback(() => {
    setChangelogStartupMode(true)
    setChangelogOpen(true)
  }, [])
  useChangelogStartup(showChangelogAtStartup)

  // Listen for custom event from Taskbar to open changelog
  const handleOpenChangelog = useCallback(() => {
    setChangelogStartupMode(false)
    setChangelogOpen(true)
  }, [])

  useEffect(() => {
    window.addEventListener('open-changelog', handleOpenChangelog)
    return () => window.removeEventListener('open-changelog', handleOpenChangelog)
  }, [handleOpenChangelog])


  return (
    <DesktopGridProvider>
      <GridDragProvider>
        <div className="flex flex-col h-screen overflow-hidden">
          <div className="flex-1 relative overflow-hidden">
            <WidgetDragProvider>
              <WidgetDndProvider>
                {children}
                <WindowContainer />
                <WidgetBar />
              </WidgetDndProvider>
            </WidgetDragProvider>
          </div>
          <Taskbar />
        </div>


        {/* Changelog Modal */}
        <ChangelogModal
          open={changelogOpen}
          onClose={() => setChangelogOpen(false)}
          isStartupMode={changelogStartupMode}
        />

        {/* Global Cmd/Ctrl+K quick-access overlay (cdb_webos#204) */}
        <CommandPalette />

        {/* "Ticket erstellen" (#1170): opened by the 'open-bug-report' /
            'open-feature-request' events, e.g. from the Spotlight row. */}
        <DesktopTicketPanel />

        {/* Selection Translation Tooltip */}
        <SelectionTranslationTooltip />
      </GridDragProvider>
    </DesktopGridProvider>
  );
}
